import { Injectable } from '@angular/core';

/**
 * Service for handling discount calculations for confirmations
 * Manages marketing, admin, and additional discounts with percentage and flat types
 * 
 * Category: Business Logic Service
 * Purpose: Discount calculations, price adjustments, totals recalculation
 */
@Injectable()
export class DiscountCalculatorService {

  constructor() { }

  /**
   * Calculates discount amount based on discount type and base amount
   * @param discount - Discount object with tipo and cantidad
   * @param baseAmount - Amount to apply discount to
   * @returns Calculated discount amount
   */
  calculateDiscountAmount(discount: any, baseAmount: number): number {
    if (!discount || !discount.tipo || !discount.cantidad) {
      return 0;
    }

    const cantidad = discount.cantidad / 1; // Convert to number

    if (discount.tipo === 'PORCENTAJE') {
      return baseAmount * (cantidad / 100);
    } else if (discount.tipo === 'FLAT') {
      return cantidad;
    }

    return 0;
  }

  /**
   * Processes a discount code and creates discount object
   * @param discountCode - Discount code ID
   * @param allDiscounts - Array of all available discounts
   * @param totalCamionetas - Total amount for vehicles
   * @param total - Total amount including adicionales
   * @param aplicarDescuento - Where to apply ('sobreTotalCamionetas' or 'sobreTotal')
   * @returns Discount object with descuentoAQuitar property
   */
  processDiscountCode(
    discountCode: string,
    allDiscounts: any[],
    totalCamionetas: number,
    total: number,
    aplicarDescuento: string
  ): any {
    if (!discountCode || discountCode === '') {
      return {};
    }

    const discount = allDiscounts.find(d => d.idc_codigos_descuento === discountCode);
    
    if (!discount) {
      return {};
    }

    const baseAmount = aplicarDescuento === 'sobreTotalCamionetas' ? totalCamionetas : total;
    const discountAmount = this.calculateDiscountAmount(discount, baseAmount);

    return {
      descuentoAQuitar: discountAmount,
      ...discount
    };
  }

  /**
   * Calculates the new totals after applying all discounts
   * @param totals - Current totals object
   * @param totalCamionetas - Total for vehicles
   * @param totalAdicionales - Total for additional concepts
   * @param adminDiscount - Admin discount object
   * @param markDiscount - Marketing discount object
   * @param additionalDiscount - Additional discount object
   * @param requiereFactura - Whether invoice is required (affects IVA)
   * @returns Updated totals object
   */
  calculateTotalsWithDiscounts(
    totals: any,
    totalCamionetas: number,
    totalAdicionales: number,
    adminDiscount: any,
    markDiscount: any,
    additionalDiscount: any,
    requiereFactura: boolean
  ): any {
    const adminAmount = adminDiscount && adminDiscount.descuentoAQuitar 
      ? adminDiscount.descuentoAQuitar 
      : 0;
    
    const markAmount = markDiscount && markDiscount.descuentoAQuitar 
      ? markDiscount.descuentoAQuitar 
      : 0;
    
    const additionalAmount = additionalDiscount && additionalDiscount.descuentoAQuitar 
      ? additionalDiscount.descuentoAQuitar 
      : 0;

    const subtotal = totalCamionetas - adminAmount - markAmount - additionalAmount + totalAdicionales;
    
    let iva = 0;
    if (requiereFactura) {
      iva = subtotal * 0.16;
    }

    const total = subtotal + iva;

    return {
      ...totals,
      subtotal: subtotal,
      iva: iva,
      total: total
    };
  }

  /**
   * Filters discounts to only include active ones (not expired)
   * @param discounts - Array of all discounts
   * @param type - Type filter ('ADMIN' or 'MARK')
   * @returns Filtered array of active discounts
   */
  filterActiveDiscounts(discounts: any[], type?: string): any[] {
    if (!discounts || discounts.length === 0) {
      return [];
    }

    const now = new Date().getTime();

    return discounts.filter(discount => {
      const expirationDate = new Date(discount.fecha_expiracion).getTime();
      const isActive = expirationDate > now;
      
      if (type) {
        return isActive && discount.mark_o_admin === type;
      }
      
      return isActive;
    });
  }

  /**
   * Creates a discount update object for database
   * @param subtotal - New subtotal
   * @param iva - New IVA
   * @param total - New total
   * @param markDiscount - Marketing discount object
   * @param adminDiscount - Admin discount object
   * @param additionalDiscount - Additional discount object
   * @returns Object with discount values to update
   */
  createDiscountUpdateObject(
    subtotal: number,
    iva: number,
    total: number,
    markDiscount: any,
    adminDiscount: any,
    additionalDiscount: any
  ): any {
    return {
      subtotal: subtotal,
      iva: iva,
      total: total,
      descuentoMarketing: markDiscount || {},
      descuentoAdmin: adminDiscount || {},
      descuentoAdicional: additionalDiscount || {}
    };
  }

  /**
   * Gets the type of a discount by its ID
   * @param discountId - Discount ID to look up
   * @param allDiscounts - Array of all discounts
   * @returns Discount type ('PORCENTAJE' or 'FLAT')
   */
  getDiscountType(discountId: string, allDiscounts: any[]): string {
    const discount = allDiscounts.find(d => d.idc_codigos_descuento === discountId);
    return discount ? discount.tipo : '';
  }

  /**
   * Gets the cantidad of a discount by its ID
   * @param discountId - Discount ID to look up
   * @param allDiscounts - Array of all discounts
   * @returns Discount cantidad
   */
  getDiscountCantidad(discountId: string, allDiscounts: any[]): any {
    const discount = allDiscounts.find(d => d.idc_codigos_descuento === discountId);
    return discount ? discount.cantidad : '';
  }

  /**
   * Resets discounts to original state (removes all discounts)
   * @param subtotalSinDescuento - Original subtotal without discounts
   * @param totalSinDescuento - Original total without discounts
   * @param requiereFactura - Whether invoice is required
   * @returns Reset discount values
   */
  resetDiscounts(
    subtotalSinDescuento: number,
    totalSinDescuento: number,
    requiereFactura: boolean
  ): any {
    let iva = 0;
    if (requiereFactura) {
      iva = subtotalSinDescuento * 0.16;
    }

    return {
      subtotal: subtotalSinDescuento,
      iva: iva,
      total: totalSinDescuento,
      descuentoMarketing: {},
      descuentoAdmin: {},
      descuentoAdicional: {}
    };
  }
}



// WEBPACK FOOTER //
// E:/Github/trosten/trosten-web-app/src/app/services/business-logic/discount-calculator.service.ts